import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { MainHeader, ProfilePanel, SearchPanel, TooltipCard } from '@components';
import { DI_TOKENS } from '@constants';
import { ChatHistoryPanel } from '@features/chat';
import { useFeatureFlag, useInjection } from '@hooks';
import { FeatureFlagKey } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  CaretDoubleRightIcon,
  ChatIcon,
  CirclesFourIcon,
  ClipboardTextIcon,
  FileMagnifyingGlassIcon,
  GearSixIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { AuthStore } from '@stores';

export const AppLayout: FC = observer(() => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTrans();
  const navigate = useNavigate();
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const isAssessmentsRoute = location.pathname.startsWith('/assessments');
  const isChatRoute = location.pathname.startsWith('/chat');
  const isRemediationRoute = location.pathname.startsWith('/remediation');
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isTenantProfileRoute = location.pathname.startsWith('/tenant-profile');
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const isPlatformAdmin = Boolean(authStore.userProfile?.isPlatformAdmin);
  const isRemediationEnabled = useFeatureFlag(FeatureFlagKey.REMEDIATION);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isSmallScreen);

  // Auto-collapse on small screens, auto-expand on large screens
  useEffect(() => {
    setIsSidebarOpen(!isSmallScreen);
  }, [isSmallScreen]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header Bar */}
      <MainHeader />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box
          sx={{
            flexShrink: 0,
            position: 'relative',
            width: isSidebarOpen ? '25%' : '64px',
            minWidth: isSidebarOpen ? '280px' : '64px',
            maxWidth: isSidebarOpen ? '380px' : '64px',
            overflow: 'hidden',
            transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 68px)',
          }}
        >
          {isSidebarOpen ? (
            // ── Full sidebar ──────────────────────────────────────────────
            <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 2, height: '100%' }}>
              <Box sx={{ backgroundColor: theme.palette.common.white, borderRadius: 4, padding: 1.5, pt: 2 }}>
                <SearchPanel onCollapse={() => setIsSidebarOpen(false)} />
              </Box>
              {!isAssessmentsRoute && !isTenantProfileRoute && !isRemediationRoute && (
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: theme.palette.common.white,
                    borderRadius: 4,
                    p: 2,
                    minHeight: '15rem',
                  }}
                >
                  <ChatHistoryPanel />
                </Box>
              )}
              <Box sx={{ backgroundColor: theme.palette.common.white, borderRadius: 4, px: 3, py: 2.5 }}>
                <ProfilePanel />
              </Box>
            </Box>
          ) : (
            // ── Mini icon bar (collapsed) ─────────────────────────────────
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 2,
                px: 1,
                gap: 1,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  backgroundColor: theme.palette.common.white,
                  borderRadius: 3,
                  py: 1,
                  px: 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  width: '100%',
                }}
              >
                {/* Expand button at top of icon stack */}
                <TooltipCard variant="dark" description={t('app.layout.sidebar.expand')} placement="right">
                  <IconButton
                    size="small"
                    onClick={() => setIsSidebarOpen(true)}
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      color: theme.palette.text.secondary,
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                    }}
                  >
                    <CaretDoubleRightIcon size={16} />
                  </IconButton>
                </TooltipCard>

                <TooltipCard variant="dark" description={t('app.layout.sidebar.dashboard')} placement="right">
                  <IconButton
                    onClick={() => navigate(routes.dashboard())}
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      color: isDashboardRoute ? theme.palette.text.primary : theme.palette.text.secondary,
                      backgroundColor: isDashboardRoute ? theme.palette.grey[200] : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                    }}
                  >
                    <CirclesFourIcon size={20} weight={isDashboardRoute ? 'fill' : 'regular'} />
                  </IconButton>
                </TooltipCard>

                <TooltipCard variant="dark" description={t('app.layout.sidebar.ask')} placement="right">
                  <IconButton
                    onClick={() => navigate('/chat')}
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      color: isChatRoute ? theme.palette.text.primary : theme.palette.text.secondary,
                      backgroundColor: isChatRoute ? theme.palette.grey[200] : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                    }}
                  >
                    <ChatIcon size={20} />
                  </IconButton>
                </TooltipCard>

                <TooltipCard variant="dark" description={t('app.layout.sidebar.assessments')} placement="right">
                  <IconButton
                    onClick={() => navigate(routes.assessmentList())}
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      color: isAssessmentsRoute ? theme.palette.text.primary : theme.palette.text.secondary,
                      backgroundColor: isAssessmentsRoute ? theme.palette.grey[200] : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                    }}
                  >
                    <ClipboardTextIcon size={20} />
                  </IconButton>
                </TooltipCard>

                {isRemediationEnabled && (
                  <TooltipCard variant="dark" description={t('app.layout.sidebar.documentsRemediation')} placement="right">
                    <IconButton
                      onClick={() => navigate(routes.remediationList())}
                      sx={{
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        color: isRemediationRoute ? theme.palette.text.primary : theme.palette.text.secondary,
                        backgroundColor: isRemediationRoute ? theme.palette.grey[200] : 'transparent',
                        '&:hover': { backgroundColor: theme.palette.grey[200] },
                      }}
                    >
                      <FileMagnifyingGlassIcon size={20} />
                    </IconButton>
                  </TooltipCard>
                )}

                <TooltipCard variant="dark" description={t('app.layout.sidebar.newChat')} placement="right">
                  <IconButton
                    onClick={() => navigate('/chat')}
                    sx={{
                      mt: 1,
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      backgroundColor: theme.palette.text.primary,
                      color: theme.palette.background.paper,
                      '&:hover': { backgroundColor: theme.palette.text.primary, opacity: 0.85 },
                    }}
                  >
                    <PlusCircleIcon size={20} weight="fill" />
                  </IconButton>
                </TooltipCard>

                {/* Thin separator */}
                <Box sx={{ width: 28, height: '1px', backgroundColor: theme.palette.grey[200], my: 0.5 }} />

                {/* Settings */}
                <TooltipCard variant="dark" description={t('app.layout.sidebar.settings')} placement="right">
                  <IconButton
                    onClick={() => navigate('/settings')}
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      color: isSettingsRoute ? theme.palette.text.primary : theme.palette.text.secondary,
                      backgroundColor: isSettingsRoute ? theme.palette.grey[200] : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.grey[200] },
                    }}
                  >
                    <GearSixIcon size={20} />
                  </IconButton>
                </TooltipCard>

                {/* Platform-admin entry — only for platform_admin users */}
                {isPlatformAdmin && (
                  <TooltipCard variant="dark" description={t('app.admin.nav.adminPanel')} placement="right">
                    <IconButton
                      onClick={() => navigate(routes.admin())}
                      sx={{
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        color: theme.palette.ocean[700],
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: theme.palette.grey[200] },
                      }}
                    >
                      <ShieldCheckIcon size={20} />
                    </IconButton>
                  </TooltipCard>
                )}
              </Box>

              <Box sx={{ flex: 1 }} />
            </Box>
          )}
        </Box>

        {/* Main Area */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 100px)',
            m: 2,
            marginInlineStart: 0,
            borderRadius: 4,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
});
